import { useCallback, useEffect, useRef, useState } from 'react'

import { Input } from '../Input/Input'
import { Select } from '../Select_Required/Select.jsx'

import areasMapper from '../../../Utils/AreasMapper.js'
import countryMapper from '../../../Utils/CountriesMapper.js'
import levelsMapper from '../../../Utils/LevelsMapper.js'
import subareasMapper from '../../../Utils/SubareasMapper.js'

import { FormattedMessage, useIntl } from 'react-intl'
import List from '../../../API/Lists/Lists.js'
import { formEETranslate } from '../../../i18n/translatedMessages.js'
import { englishList, formationList, seniorityList } from './lists.js'

import { translate as translateAreas } from '../../../i18n/Library/areasCorrelation.js'
import { translate as translateEnglishLevel } from '../../../i18n/Library/englishLevel.js'
import { translate as translateFormation } from '../../../i18n/Library/formationTranslate.js'
import { translate as translatePosition } from '../../../i18n/Library/hierarchicalLevel'
import { translate as translateSenority } from '../../../i18n/Library/senorityTranslate.js'
import { translate as translateSubAreas } from '../../../i18n/Library/subareasTranslate.js'
import { SalarySelectorModal } from './SalarySelectorModal'

import jobMatch from '../../../API/JobMatch/jobMatch'
import { FiChevronDown } from 'react-icons/fi'
import Loading from '../../Loading/Loading'

const loggedEmail = localStorage.getItem('email')
const isMayekawa = !!loggedEmail?.includes('@mayekawa')

export function FormPositions({ handleChange, user, setUser, errors, emailDisabled = false, title }) {
	const getLanguage = useIntl()
	const language = getLanguage.locale === 'PT-BR' ? 'pt' : 'es'
	const companyName = JSON.parse(localStorage.getItem('user'))?.name
	const [countries, setCountries] = useState([])
	const [areas, setAreas] = useState([])
	const [relatedSubareas, setRelatedSubareas] = useState([])
	const [subareas, setSubareas] = useState([])
	const [levels, setLevels] = useState([])
	const [modalOpen, setModalOpen] = useState(false)
	const [selectedSalary, setSelectedSalary] = useState(null)
	const [adjustedSalaryOptions, setAdjustedSalaryOptions] = useState([])
	const [marketSalaries, setMarketSalaries] = useState([])
	const [factors, setFactors] = useState([])
	const [loading, setLoading] = useState(false)

	const lastMarketQuery = useRef({ country: null, level: null, area: null })

	const load = useCallback(async () => {
		try {
			setLoading(true)

			const promises = [List.areas(), List.countries(), List.levels(), List.subareas(), jobMatch.getFactors(companyName)]

			let matchPromise = Promise.resolve(null)
			if (user?.mydna_email_position) {
				matchPromise = jobMatch.getMatchId(user.mydna_email_position)
				promises.push(matchPromise)
			}

			const [resArea, resCountry, resLevel, resSubareas, resFactors, jobMatchResponse] = await Promise.all(promises)

			if (jobMatchResponse) {
				setUser(prev => ({
					...prev,
					...jobMatchResponse,
				}))
			}

			setCountries(resCountry.map(countryMapper.toDomain))
			setAreas(resArea.map(areasMapper.toDomain))
			setLevels(resLevel.map(levelsMapper.toDomain))
			setSubareas(resSubareas.map(subareasMapper.toDomain))
			setFactors(resFactors)
		} finally {
			setLoading(false)
		}
	}, [companyName, user?.mydna_email_position])

	useEffect(() => {
		load()
	}, [load])

	const handlePosition = event => {
		const position = event.target.value.toLowerCase().split(' ')

		const [get_level] = levels.filter(item => position.includes(item.value.toLowerCase()))
		const [get_subarea] = subareas.filter(item => position.includes(item.value.toLowerCase()))

		setUser({
			...user,
			position: event.target.value,
			level: get_level?.id,
			subarea: get_subarea?.id || user?.subarea,
			subareaName: get_subarea?.value || user?.subareaName,
			area: get_subarea?.area,
		})
	}

	const handleSubareas = event => {
		const subareaId = event.target.value
		const subarea = subareas.find(item => item.id === subareaId)

		setUser({
			...user,
			subarea: subarea?.id,
			subareaName: subarea?.value,
		})
	}

	const handleAreas = event => {
		setUser({
			...user,
			[event.target.name]: event.target.value,
			subarea: null,
			subareaName: null,
		})
	}

	useEffect(() => {
		if (user?.area) {
			setRelatedSubareas(translatedSubAreas.filter(item => item.area === user.area))
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user?.area, subareas])

	const calculateAdjustedSalaries = useCallback(() => {
		setAdjustedSalaryOptions([])
		if (!factors || !marketSalaries.length) return

		const size = factors?.company_info?.company_size_id
		const getFactor = (arr, level, sizeKey) => {
			if (!Array.isArray(arr)) return 1

			if (!level) return arr.find(f => f.level === 2 && (!sizeKey || f.size === sizeKey))?.factor || 1

			const match = arr.find(f => f.level === level && (!sizeKey || f.size === sizeKey))
			if (match) return match.factor

			const middle = arr.find(f => f.level === 2 && (!sizeKey || f.size === sizeKey))
			return middle?.factor || 1
		}

		const seniorityFactor = getFactor(factors.seniority_factors, user?.experience, size)
		const qualificationFactor = getFactor(factors.qualification_factors, user?.training)
		const languageFactor = getFactor(factors.language_factors, user?.english)

		const adjusted = Array.isArray(marketSalaries)
			? marketSalaries.map(item => {
					const base = Number(item.salary)
					const adjustedValue = base * seniorityFactor * qualificationFactor * languageFactor
					return {
						id: item.id,
						name: item.position,
						fixedSalary: adjustedValue,
						variableSalary: item.variable_salary,
					}
			  })
			: []

		setAdjustedSalaryOptions(adjusted)

		if (user?.job_id_smtm) {
			const match = adjusted.find(opt => opt.id === Number(user.job_id_smtm))
			if (match) {
				setSelectedSalary(match)
				setUser(prev => ({
					...prev,
					salary_market: match.fixedSalary,
					variableSalary: match.variableSalary,
				}))
			}
		}
	}, [factors, marketSalaries, user?.experience, user?.training, user?.english, user?.job_id_smtm, setUser])

	const fetchSalariesIfNeeded = useCallback(async () => {
		setSelectedSalary(null)

		if (user?.country && user?.level && user?.area) {
			const { country, level, area } = user

			lastMarketQuery.current = { country, level, area }

			const res = await jobMatch.getMarketSalaries(country, level, area, companyName)
			setMarketSalaries(res.length > 0 ? res : [])
		}
	}, [user?.country, user?.level, user?.area, companyName])

	useEffect(() => {
		fetchSalariesIfNeeded()
	}, [fetchSalariesIfNeeded])

	useEffect(() => {
		calculateAdjustedSalaries()
	}, [calculateAdjustedSalaries])

	useEffect(() => {
		if (!selectedSalary || !adjustedSalaryOptions.length) return

		const updated = adjustedSalaryOptions.find(opt => opt.id === selectedSalary.id)
		if (updated) {
			setSelectedSalary(prev => ({
				...prev,
				fixedSalary: updated.fixedSalary,
			}))
		}
	}, [adjustedSalaryOptions, selectedSalary?.id])

	const translatedAreas = [...areas]
		.sort((a, b) => a.value.localeCompare(b.value))
		.map(item => ({
			...item,
			value: translateAreas[language][item?.value],
		}))

	const translatedLevels = [...levels]
		.sort((a, b) => a.value.localeCompare(b.value))
		.map(item => ({
			...item,
			value: translatePosition[language][item?.value],
		}))

	const translatedEnglishLevel = englishList.map(item => ({
		...item,
		value: translateEnglishLevel[language][item?.value],
	}))

	const translatedFormation = formationList.map(item => ({
		...item,
		value: translateFormation[language][item?.value],
	}))

	const translatedSenority = seniorityList.map(item => ({
		...item,
		value: translateSenority[language][item?.value],
	}))

	const translatedSubAreas = subareas.map(item => ({
		...item,
		value: translateSubAreas[language][item?.value],
	}))

	const normalizeSubareaFromUser = (user, subareas) => {
		const raw = user.subareaName
		if (!raw) return user

		let match
		if (!isNaN(raw)) {
			match = subareas.find(s => s.id === Number(raw))
		} else {
			match = subareas.find(s => s.value === raw)
		}

		if (!match) return user

		return {
			...user,
			subarea: match.id,
			subareaName: match.value,
		}
	}

	useEffect(() => {
		if (user?.subareaName && subareas.length > 0) {
			setUser(prev => normalizeSubareaFromUser(prev, subareas))
		}
	}, [user?.subareaName, subareas])

	const handleSelect = option => {
		setSelectedSalary(option)
		setUser(prev => ({
			...prev,
			job_id: option.id,
			salary_market: option.fixedSalary,
		}))
	}

	const formatNumber = value => {
		if (value === null || value === undefined || isNaN(value)) return ''
		return new Intl.NumberFormat('en-US', {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		}).format(Number(value))
	}

	return (
		<>
			<div className='row'>
				<div className='col'>
					<h5 className='fw-bolder mb-3'>{title || formEETranslate.EEpersonalInformation}</h5>
				</div>
			</div>
			{loading ? (
				<Loading />
			) : (
				<>
					<div className='row my-1'>
						<div className='col'>
							<FormattedMessage id={'EEPositionPlaceholder'}>
								{msg => (
									<Input
										label={formEETranslate.EEFormPosition}
										placeholder={msg}
										onChange={handlePosition}
										name='position'
										value={user?.position || ''}
										required
										error={errors?.includes('position')}
									/>
								)}
							</FormattedMessage>
						</div>
						<div className='col'>
							<Select
								label={formEETranslate.EEFormNivel}
								placeholder={formEETranslate.EEFormSelect}
								onChange={handleChange}
								name='level'
								value={user?.level}
								required
								error={errors?.includes('level')}
								list={translatedLevels}
							/>
						</div>
					</div>

					<div className='row my-1'>
						<div className='col'>
							<Select
								label={formEETranslate.EEFormCountry}
								placeholder={formEETranslate.EEFormSelect}
								onChange={handleChange}
								name='country'
								value={user?.country}
								required
								list={countries}
								error={errors?.includes('country')}
							/>
						</div>
						<div className='col'>
							<Select
								label={formEETranslate.EEFormRegion}
								placeholder={formEETranslate.EEFormSelect}
								onChange={handleChange}
								name='region'
								value={user?.region}
								required
								list={countries}
								error={errors?.includes('region')}
								disabled
							/>
						</div>
					</div>

					<div className='row my-1'>
						<div className='col'>
							<Select
								label={formEETranslate.EEFormArea}
								placeholder={formEETranslate.EEFormSelect}
								onChange={handleAreas}
								name='area'
								value={user?.area}
								required
								error={errors?.includes('area')}
								list={translatedAreas}
							/>
						</div>
						<div className='col'>
							<Select
								label={formEETranslate.EEFormSubarea}
								placeholder={formEETranslate.EEFormSelect}
								onChange={handleSubareas}
								name='subarea'
								value={user?.subarea}
								required
								error={errors?.includes('subarea')}
								list={relatedSubareas}
								disabled={relatedSubareas.length === 0}
							/>
						</div>
					</div>
					<div className='row my-1'>
						<div className='col'>
							<Select
								label={formEETranslate.EEFormEnglishLevel}
								placeholder={formEETranslate.EEFormSelect}
								onChange={handleChange}
								name='english'
								value={user?.english}
								list={translatedEnglishLevel}
							/>
						</div>
						<div className='col'>
							<Select
								label={formEETranslate.EEFormFormation}
								placeholder={formEETranslate.EEFormSelect}
								onChange={handleChange}
								name='training'
								value={user?.training}
								list={translatedFormation}
							/>
						</div>
					</div>
					<div className='row my-1'>
						<div className='col'>
							<Select
								label={formEETranslate.EEFormFunction}
								placeholder={formEETranslate.EEFormSelect}
								onChange={handleChange}
								name='experience'
								value={user?.experience}
								list={translatedSenority}
							/>
						</div>
						{!isMayekawa && (
							<div className='col'>
								{
									<Input
										label={formEETranslate.EEFormSalaryFix}
										placeholder='Valor'
										onChange={handleChange}
										name='salary'
										value={user?.salary || ''}
									/>
								}
							</div>
						)}
					</div>
					{!isMayekawa && (
						<div className='row my-1'>
							<div className='col'>
								<FormattedMessage id={'EESalaryVariablePlaceholder'}>
									{msg => (
										<Input
											label={formEETranslate.EEFormSalaryVariable}
											placeholder={msg}
											onChange={handleChange}
											name='variableSalary'
											value={user?.variableSalary || ''}
										/>
									)}
								</FormattedMessage>
							</div>
							<div className='col'>
								<div className=''>
									<div className='tw-font-bold tw-text-[14px]'>
										Salario medio del mercado <span className='text-primary'>*</span>
									</div>
									<div
										className='tw-w-full tw-h-[45px] tw-bg-white tw-rounded-md tw-py-2 tw-px-3 tw-flex tw-justify-between tw-items-center tw-cursor-pointer'
										onClick={() => setModalOpen(true)}
										style={{ borderRadius: '8px', border: '1px solid #CBD5E1', borderLeft: '4px solid #465568' }}
									>
										<span className={`tw-text-base ${!selectedSalary ? 'tw-text-gray-400' : ''}`}>
											{selectedSalary ? `${selectedSalary.name} - ${formatNumber(selectedSalary.fixedSalary)}` : 'Seleccionar'}
										</span>
										<FiChevronDown className='tw-text-gray-500 tw-ml-2' />
									</div>

									<SalarySelectorModal
										isOpen={modalOpen}
										onClose={() => setModalOpen(false)}
										onSelect={handleSelect}
										options={adjustedSalaryOptions}
										selected={selectedSalary?.id}
										user={user}
										areaList={translatedAreas}
										levelList={translatedLevels}
										byPosition={true}
									/>
								</div>
							</div>
						</div>
					)}
				</>
			)}
		</>
	)
}
